import {
  BzitResponse,
  BzitSearchLocation,
  BzitSearchLocations,
  BzitSearchParameters,
  BzitVehicleSearch,
  SearchParams,
  SearchResult,
  ServiceResult
} from '../../bzit';
import { AdditionalSearchParams } from '../../configs';
import { introSearchForm } from '../../configs/additionalSearchParams';
import { userCountry, userLanguage } from '../../configs/env';
import { searchForm } from '../../configs/searchForm';
import { reportError } from '../../error-handling/reportError';
import * as SearchService from '../../helper/fetch/SearchService';
import { Maybe } from '../../types';
import { keys } from '../../utils/Object';
import { Translate } from '../Typo3Provider/types';
import { searchVehicles } from './fetcher';
import { defaultSortingOption } from './sorting';
import {
  SearchLocations,
  SearchLocationsInCountry,
  SearchState
} from './types';

export const locationsFromSearchData = ({
  locations
}: BzitSearchParameters): SearchLocations => {
  const values: BzitSearchLocation[][] = Object.keys(locations).map(
    key => locations[key] || []
  );
  const items = values.map(location =>
    Object.assign({}, ...location.map(item => ({ [item.code]: item.name })))
  );
  return Object.assign({}, ...items);
};

export const getSortedLocationCodes = (
  locations: BzitSearchLocations,
  languageCode: string
): string[] => {
  const empty: BzitSearchLocation[] = [];
  const values: BzitSearchLocation[] = empty.concat(
    ...keys(locations).map(key => locations[key] || [])
  );
  values.sort((l0: BzitSearchLocation, l1: BzitSearchLocation) =>
    l0.name.localeCompare(l1.name, languageCode)
  );

  const locationCodes = values.map(({ code }) => code);
  let sortedLocationCodes = locationCodes;

  // Custom sorting for specific locations #57914
  const useCustomSortingFrance = locationCodes.includes('ALB') && locationCodes.includes('PAR');
  const useCustomSortingGreece = locationCodes.includes('THE') && locationCodes.includes('ATH');

  if (useCustomSortingFrance || useCustomSortingGreece) {
    sortedLocationCodes = sortedLocationCodes.reduce<string[]>((agg, cur) => {
      if (useCustomSortingFrance && cur === 'ALB') {
        return agg;
      }

      if (useCustomSortingFrance && cur === 'PAR') {
        return [...agg, 'PAR', 'ALB'];
      }

      if (useCustomSortingGreece && cur === 'THE') {
        return agg;
      }

      if (useCustomSortingGreece && cur === 'ATH') {
        return [...agg, 'ATH', 'THE'];
      }

      return [...agg, cur];
    }, []);
  }

  return sortedLocationCodes;

};

export const getSortedCountriesWithLocations = (
  searchParameters: BzitSearchParameters,
  translateUsedVehicles: Translate,
  languageCode: string
): SearchLocationsInCountry[] => {
  const { locations } = searchParameters;
  const countries = Object.keys(locations).map(countryId => {
    const country = translateUsedVehicles(countryId);
    const locationList = locations[countryId] || [];
    const locationCodes = locationList.map(({ code }) => code);
    const countryCode = countryId.split('-')[1];
    return {
      country,
      countryId,
      countryCode,
      locationCodes
    };
  });
  countries.sort((c0: SearchLocationsInCountry, c1: SearchLocationsInCountry) =>
    c0.country.localeCompare(c1.country, languageCode)
  );
  return countries;
};

export const emptyState = (): SearchState => ({
  countryId: '',
  searchParameters: {
    lastUpdate: '',
    locations: {},
    nameCurrency: [],
    nameEuronorm: [],
    nameManufacturer: {},
    nameManufacturerLong: {},
    nameManufacturerShort: {}
  },
  countUsage: {
    bodies: {},
    usages: {}
  },
  locations: {},
  locationsGroupedByCountries: [],
  countryIdByLocationId: new Map(),
  sortedLocationCodes: [],
  allVehicles: [],
  formParams: { ...searchForm },
  additionalSearchParams: { ...introSearchForm },
  sorting: defaultSortingOption,
  paramsLoaded: false,
  resultsLoaded: false,
  resultsAreDirty: false,
  searchIsReady: false,
  paramsError: null,
  resultsError: null,
  apiSearchResult: [],
  searchResult: [],
  alternativeOffers: [],
  detailNavigationOffers: [],
  locale: `${userLanguage}-${userCountry}`,
  setCountryId: id => {},
  setSearchFormData: fd => {},
  setAdditionalSearchParams: fd => {},
  setSorting: s => {},
  setDetailNavigationOffers: xs => {},
  resetDetailNavigationOffers: () => {},
  getVehicleList: s => Promise.resolve([]),
  getOffersByLocation: s => Promise.resolve([]),
  getOffersByCountry: s => Promise.resolve([]),
  getOffersByManufacturer: xs => Promise.resolve([]),
});

export const mkSearchParams = (
  params: Partial<SearchParams>
): SearchParams => ({
  ...searchForm,
  ...params
});

export const mkAdditionalSearchParams = (
  params: Partial<AdditionalSearchParams>
): AdditionalSearchParams => ({
  ...introSearchForm,
  ...params
});

export const search = async (
  params: SearchParams
): Promise<SearchResult> => {
  try {
    const response: ServiceResult<SearchResult> = await SearchService.search(
      params
    );
    if (response.success) {
      return response.result;
    } else {
      throw response.error;
    }
  } catch (e) {
    reportError('SearchProvider/utils.search()', e);
  }
  return {
    vehicles: [],
    vehiclesSimilar: [],
  };
};

export const getVehicleList = async (
  formParams: SearchParams,
  vehicleIds: string[]
): Promise<BzitVehicleSearch[]> => {
  const { nameCurrency } = formParams;
  const response = await searchVehicles(
    mkSearchParams({
      ...{ nameCurrency },
      vehicleIds
    })
  );

  if (response.success) {
    return response.result.vehicles;
  }

  throw response.error;
};

export const getOffersByLocation = async (
  formParams: SearchParams,
  codeLocation: string
): Promise<BzitVehicleSearch[]> => {
  const { nameCurrency, codeWheelpos } = formParams;
  const response = await searchVehicles(
    mkSearchParams({
      ...{ nameCurrency, codeWheelpos },
      codeLocation
      // 20180801 - show all vehicles, not only offers
      // onlyOfferVehicles: true
    })
  );

  if (response.success) {
    return response.result.vehicles;
  }

  throw response.error;
};

export const getOffersByManufacturer = async (
  formParams: SearchParams,
  nameManufacturer: string
): Promise<BzitVehicleSearch[]> => {
  const { nameCurrency, codeWheelpos } = formParams;
  const response = await searchVehicles(
    mkSearchParams({
      ...{ nameCurrency, codeWheelpos },
      namesManufacturer: [nameManufacturer]
      // 20180801 - show all vehicles, not only offers
      // onlyOfferVehicles: true
    })
  );

  if (response.success) {
    return response.result.vehicles;
  }

  throw response.error;
};

export function extractResult<A>({
  resultCode,
  resultMsg,
  ...result
}: BzitResponse<A>): A {
  // typescript cannot do this, unlike FlowType
  // this is sad
  const a: A = result as any;
  return a;
}

export const languageCodeCorrections: { [key: string]: Maybe<string> } = {
  dk: 'da'
};

export const countryCodeCorrections: { [key: string]: Maybe<string> } = {};

/**
 *
 * @param {SearchParams} formParams
 * @returns {boolean} returns `true` if any member of the given `formParams` has a value other than
 * any falsy value (`'', 0, false undefined, null`) or an empty list (`[]`), ignoring the field `nameCurrency`
 */
export const hasValidSearchCriteria = (formParams: SearchParams) => {
  return keys(formParams).reduce((valid: boolean, key: keyof SearchParams) => {
    const value = formParams[key];
    if (key === 'nameCurrency') {
      return valid;
    }
    const valueType = typeof value;
    switch (valueType) {
      case 'string':
      case 'number':
      case 'boolean':
        return valid || !!value;
      default:
        if (Array.isArray(value)) {
          return valid || !!value.length;
        }
        return valid;
    }
  }, false);
};
